<template #default>
  <div class="page-view">
    <a-card class="page-filter" :bordered="false">
      <a-form
        layout="horizontal"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
        @submit.prevent="onSearch"
      >
        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="InstClass">
              <a-tree-select
                v-model:value="query.externalInstClass"
                :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
                style="width: 100%"
                allow-clear
                tree-default-expand-all
                placeholder="Please select"
                :show-search="true"
                :tree-data="instClassOptions"
                tree-node-filter-prop="label"
              >
                <template #title="{ label }"> {{ label }}</template>
              </a-tree-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Exchange">
              <a-select
                v-model:value="query.exchangeCode"
                style="width: 100%"
                allow-clear
                placeholder="All"
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in exchangeList"
                  :key="item.exchName"
                  :value="item.exchName"
                >
                  {{ item.exchName }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Name">
              <a-input
                v-model:value="query.displayCode"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Description">
              <a-input
                v-model:value="query.securityDesc"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Sec Id">
              <a-input
                v-model:value="query.secId"
                @pressEnter="onSearch"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item label="Identifier Type">
              <a-select
                v-model:value="query.identType"
                allow-clear
                :show-search="true"
                :filter-option="filterOption"
              >
                <a-select-option
                  v-for="item in identifierTypeList"
                  :key="item"
                  :value="item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Identifier">
              <a-auto-complete
                v-model:value="query.identifier"
                :options="tickerOptions"
                @search="onSearchTicker"
                @select="onSearchTicker"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16" align="middle">
          <a-col :span="12">
            <a-form-item
              :colon="false"
              :labelCol="{ span: 6, style: { textAlign: 'left' } }"
              :wrapperCol="{ span: 18 }"
            >
              <div class="page-actions">
                <a-button type="primary" @click="onSearch" :loading="loading"
                  >Search</a-button
                >
                <a-button @click="showAddSwapModal">
                  <PlusOutlined />
                  Add Swap
                </a-button>
                <a-button @click="showCopyModal">Copy</a-button>
              </div>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <div class="page-grid page-grid--h540">
      <AgGridVue
        style="width: 100%; height: 100%"
        :theme="themeBalham"
        :loading="loading"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
      />
    </div>
    <AddEditSwapView
      v-model:visible="addSwapModalVisible"
      :isEdit="isEdit"
      :addForm="addForm"
      @update:security="handleUpdateSecurity"
    />
    <IdentifierView
      v-model:visible="identifierModalVisible"
      :rawData="identifierRawData"
      @update:identifiers="handleUpdateIdentifiers"
    />
  </div>
</template>

<script setup>
import { ref, defineExpose, onMounted, provide, computed } from 'vue'
// import { useStore } from 'vuex'
import { themeBalham } from 'ag-grid-community'

import { message } from 'ant-design-vue'
import { PlusOutlined } from '@ant-design/icons-vue'

import { buildSwapColumnDefs } from './SwapView_ColumnDefs'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import AddEditSwapView from './AddEditSwapView.vue'
import IdentifierView from '../IdentifierView.vue'

import reference from '@/api/reference'
import organization from '@/api/organization'
import custodianAccount from '@/api/custodianAccount'
import security from '@/api/security'
import dayjs from 'dayjs'
import { mapIdentifierField } from '@/utils/secutils'
import debounce from 'lodash/debounce'

// const store = useStore()
const gridApi = ref(null)
const gridOptions = {
  rowSelection: 'single',

  onGridReady: (params) => {
    gridApi.value = params.api
  },
}

const defaultColDef = {
  filter: true,
  sortable: true,
  resizable: true,
}

defineExpose({
  AgEditCellRenderer,
})

const labelCol = {
  span: 6,
  style: { textAlign: 'left' },
}

const wrapperCol = {
  span: 18,
}

const loading = ref(false)
const rowData = ref([])
const instClassOptions = ref([])
const swapInstClassList = ref([])
const exchangeList = ref([])
const identifierTypeList = ref([])
const tickerOptions = ref([])
const tickerList = ref([])
const issuerList = ref([])
const custodianList = ref([])
const custodianAccountList = ref([])
const identifierRawData = ref({
  securityId: null,
  identifiers: [],
})
const addSwapModalVisible = ref(false)
const addForm = ref({})
const isEdit = ref(false)
const identifierModalVisible = ref(false)

const query = ref({
  assetClass: 'SWAP',
  externalInstClass: null,
  exchangeCode: null,
  displayCode: null,
  securityDesc: null,
  secId: null,
  identType: null,
  identifier: null,
  effectiveDate: null,
})

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback

const searchTicker = (ticker) => {
  if (!ticker || ticker.length < 3 || !query.value.identType) {
    return
  }
  const identType = query.value.identType
  security
    .getSimpleSecurities({
      tickerOperator: 'START_WITH',
      identType: identType,
      identifier: ticker,
    })
    .then((res) => {
      const data = Array.isArray(res.data) ? res.data : []
      tickerList.value = data
      const tickers = data
        .filter((item) => {
          return mapIdentifierField(item, identType)
        })
        .map((item) => {
          return mapIdentifierField(item, identType)
        })
      tickers.sort((a, b) => b.localeCompare(a))
      const arr = tickers.length > 8 ? tickers.slice(0, 8) : tickers
      tickerOptions.value = arr.map((item) => ({ value: item }))
    })
}
const onSearchTicker = debounce(
  function (ticker) {
    searchTicker(ticker)
  },
  600,
  { maxWait: 1000 }
)
const onSearch = async () => {
  const params = { ...query.value }
  const allOtherFieldsEmpty =
    !params.exchangeCode &&
    (!params.displayCode || !params.displayCode.trim()) &&
    (!params.securityDesc || !params.securityDesc.trim()) &&
    !params.secId &&
    (!params.identifier || !params.identifier.trim())

  if (
    allOtherFieldsEmpty &&
    (!params.externalInstClass || params.externalInstClass === 'SWAP')
  ) {
    return
  }
  loading.value = true
  try {
    if (params.externalInstClass === 'SWAP') {
      params.assetClass = params.externalInstClass
      params.externalInstClass = null
    }

    if (!params.identifier || !params.identifier.trim()) {
      params.identType = null
    }
    params.tickerOperator = 'START_WITH'

    const resp = await security.getSimpleSecurities(params)
    const data = resp.data
    rowData.value = Array.isArray(data) ? data : []
    if (rowData.value.length === 0) {
      message.info('No data found.')
    }
  } catch (e) {
    message.error(getErrorMessage(e, 'Query failed'))
  } finally {
    loading.value = false
  }
}

const showAddSwapModal = () => {
  showAddModal('SWAP')
}

const showAddModal = (assetClass) => {
  isEdit.value = false
  addForm.value = {
    securityId: null,
    externalSecurityId: null,
    assetClass: assetClass,
    issueDate: null,
    settleDate: null,
    maturityDate: null,
    exchange: {
      exchangeId: null,
      exchName: null,
      micExchName: null,
    },
    issuer: {
      issuerId: null,
      shortName: null,
      name: null,
    },
    underlyingTicker: null,
    instrumentClass: null,
    isin: null,
    ticker: null,
    name: null,
    desc: null,
    priceMultiplier: 1,
    contractSize: 1,
    tickSize: 1,
    tickValue: 1,
    pnlCurrency: null,
    isActive: true,
  }

  addForm.value.swapType = 'NOTIONAL'
  addForm.value.fixedFloat = 'F'
  addForm.value.dividendIssueFXRate = 1
  addForm.value.fixedRateMdInd = 'M'
  addForm.value.fixedRate = 1
  addForm.value.conversionRatio = 1
  addForm.value.financingBasis = 'NOTIONAL_MKT_VAL_TD'
  addForm.value.realisePLTiming = 'UNWIND'
  addForm.value.dividendPaymentTiming = 'PAY_DATE'
  addForm.value.accountForDividendsFrom = 'SCEV_PAY_DATE'
  addForm.value.underlyingDividendPercent = 100
  addForm.value.equityResetFrequencyName = 'None'
  addSwapModalVisible.value = true
}
const showCopyModal = async () => {
  const selectedRows = gridApi.value?.getSelectedRows?.() || []
  if (selectedRows.length !== 1) {
    message.warning('Please select one row to copy')
    return
  }

  const params = { securityId: selectedRows[0].securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      const data = resp.data
      addForm.value = formatDateTime({
        ...data,
        securityId: undefined,
        externalSecurityId: undefined,
        exchange: { ...data.exchange },
        issuer: { ...data.issuer },
        identifiers: data.identifiers ? [...data.identifiers] : [],
        underlying: data.underlying ? { ...data.underlying } : null,
      })
      isEdit.value = false
      addSwapModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}
const formatDateTime = (security) => {
  const format = (val) => (val ? dayjs(dayjs(val).format('YYYY-MM-DD')) : null)

  addForm.value.issueDate = format(security.issueDate)
  security.settleDate = format(security.settleDate)
  security.maturityDate = format(security.maturityDate)
  security.firstTradeDate = format(security.firstTradeDate)
  security.lastTradeDate = format(security.lastTradeDate)
  security.firstNoticeDate = format(security.firstNoticeDate)
  security.firstExerciseDate = format(security.firstExerciseDate)
  security.accrualDate = format(security.accrualDate)
  security.firstCouponDate = format(security.firstCouponDate)

  return security
}

const showEditModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    if (resp.data) {
      addForm.value = formatDateTime(resp.data)
      if (addForm.value.priceMultiplier) {
        addForm.value.priceDivisor = 1 / addForm.value.priceMultiplier
      }
      isEdit.value = true

      addForm.value.fixedFloat = addForm.value.fixedFloat ?? 'F'
      addForm.value.dividendIssueFXRate = addForm.value.dividendIssueFXRate ?? 1
      addForm.value.fixedRateMdInd = addForm.value.fixedRateMdInd ?? 'M'
      addForm.value.fixedRate = addForm.value.fixedRate ?? 1
      addForm.value.conversionRatio = addForm.value.conversionRatio ?? 1
      addForm.value.financingBasis =
        addForm.value.financingBasis ?? 'NOTIONAL_MKT_VAL_TD'
      addForm.value.realisePLTiming = addForm.value.realisePLTiming ?? 'UNWIND'
      addForm.value.dividendPaymentTiming =
        addForm.value.dividendPaymentTiming ?? 'PAY_DATE'
      addForm.value.accountForDividendsFrom =
        addForm.value.accountForDividendsFrom ?? 'SCEV_PAY_DATE'
      addForm.value.underlyingDividendPercent =
        addForm.value.underlyingDividendPercent ?? 100
      addForm.value.equityResetFrequencyName =
        addForm.value.equityResetFrequencyName ?? 'None'
      addSwapModalVisible.value = true
    }
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const handleUpdateSecurity = async (newSecurity, isEdit) => {
  loading.value = true
  try {
    if (isEdit) {
      await security.updateSec(newSecurity)
      message.success('Security updated successfully')
      return
    }
    await security.createSec(newSecurity)
    message.success('Security created successfully')
  } catch (error) {
    message.error(
      getErrorMessage(
        error,
        isEdit ? 'Failed to update security' : 'Failed to create security'
      )
    )
  } finally {
    loading.value = false
  }
}

const filterOption = (input, option) => {
  const key = option?.key ?? option?.value ?? ''
  return String(key).toLowerCase().includes(input.toLowerCase())
}

const showIdentifierModal = async (event) => {
  const data = event?.data || event
  const params = { securityId: data.securityId }
  try {
    const resp = await security.getSecurity(params)
    identifierRawData.value.securityId = data.securityId
    identifierRawData.value.identifiers = resp.data.identifiers || []
    identifierModalVisible.value = true
  } catch (error) {
    message.error(getErrorMessage(error, 'Failed to fetch security details'))
  }
}

const columnDefs = computed(() =>
  buildSwapColumnDefs({
    onIdentifiers: showIdentifierModal,
    onEdit: showEditModal,
  })
)

const handleUpdateIdentifiers = () => {}

onMounted(async () => {
  try {
    const [referenceDataRes, custodianRes, custodianAccountRes] =
      await Promise.all([
        reference.getReferenceDataV2(),
        organization.getOrganizations({
          organizationCode: null,
          organizationTypeCode: 'CUSTODIAN',
          statusCode: 'A',
        }),
        custodianAccount.getCustodianAccounts({
          accountCode: null,
          fundCode: null,
          custodianCode: null,
          isActive: true,
        }),
      ])

    swapInstClassList.value = referenceDataRes.data?.swapTypes || []
    instClassOptions.value = [
      {
        label: 'SWAP',
        value: 'SWAP',
        // disabled: true,
        // selectable: false,
        children: swapInstClassList.value.map((item) => ({
          label: item,
          value: item,
        })),
      },
    ]
    exchangeList.value = referenceDataRes.data?.exchanges || []
    identifierTypeList.value = referenceDataRes.data?.identifierTypes || []
    issuerList.value = referenceDataRes.data?.issuers || []
    custodianList.value =
      custodianRes.data.map((r) => ({
        custodianId: r.organizationId,
        custodianCode: r.organizationCode,
      })) || []
    custodianAccountList.value = custodianAccountRes.data || []
  } catch (e) {
    message.error(getErrorMessage(e, 'Failed to fetch reference lists'))
  }
})

provide('exchangeList', exchangeList)
provide('issuerList', issuerList)
provide('swapInstClassList', swapInstClassList)
provide('custodianList', custodianList)
provide('custodianAccountList', custodianAccountList)
provide('identifierTypeList', identifierTypeList)
provide('security', security)
</script>
